import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PixelCanvas } from '../components/PixelCanvas';
import { ArrowLeft, ZoomIn, ZoomOut, Hand, Clock, CheckCircle2, XCircle, Menu } from 'lucide-react';
import { toast } from 'sonner';
import type { Recipe, Canvas } from '../types';

export default function RecipePage() {
  const { id: recipeId } = useParams<{ id: string }>();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Canvas navigation
  const [zoom, setZoom] = useState(6);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hover coordinates
  const [hoverCoords, setHoverCoords] = useState<{ x: number; y: number } | null>(null);

  // Authentication & actions
  const [token, setToken] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Check for auth token
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  useEffect(() => {
    if (!recipeId) return;

    const fetchData = async () => {
      try {
        const [recipeRes, canvasRes] = await Promise.all([
          fetch(`/api/recipes/${recipeId}`),
          fetch(`/api/canvas`)
        ]);

        if (!recipeRes.ok) {
          throw new Error('Recipe not found');
        }

        const recipeData = await recipeRes.json();
        const canvasData = await canvasRes.json();

        setRecipe(recipeData);
        setCanvas(canvasData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load recipe');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [recipeId]);

  // Center canvas on load
  useEffect(() => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  }, [canvas, zoom]);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 1 : -1;
      setZoom(prev => Math.max(2, Math.min(12, prev + delta)));
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setLastTouchDistance(getTouchDistance(e.touches));
    } else if (e.touches.length === 1) {
      setIsPanning(true);
      setPanStart({ 
        x: e.touches[0].clientX - offset.x, 
        y: e.touches[0].clientY - offset.y 
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches);
      if (newDistance) {
        const scale = newDistance / lastTouchDistance;
        setZoom(prev => Math.max(2, Math.min(12, prev * scale)));
        setLastTouchDistance(newDistance);
      }
    } else if (e.touches.length === 1 && isPanning) {
      setOffset({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    setLastTouchDistance(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const resetView = () => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  };

  const handlePixelHover = (x: number | null, y: number | null) => {
    if (x !== null && y !== null) {
      setHoverCoords({ x, y });
    } else {
      setHoverCoords(null);
    }
  };

  const handleAccept = async () => {
    if (!token || !recipeId || !recipe) return;

    setProcessing(true);
    try {
      const response = await fetch(`/api/recipes/${recipeId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to accept recipe');
      }

      setRecipe({ ...recipe, status: 'accepted', processedAt: Date.now() });
      toast.success('Rezept akzeptiert!', {
        description: 'Canvas wurde für alle Zuschauer aktualisiert.'
      });
    } catch (err) {
      console.error('Error accepting recipe:', err);
      toast.error('Fehler beim Akzeptieren', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token || !recipeId || !recipe) return;

    setProcessing(true);
    try {
      const response = await fetch(`/api/recipes/${recipeId}/decline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to decline recipe');
      }

      setRecipe({ ...recipe, status: 'declined', processedAt: Date.now() });
      toast.success('Rezept abgelehnt', {
        description: 'Das Rezept wurde aus der Warteschlange entfernt.'
      });
    } catch (err) {
      console.error('Error declining recipe:', err);
      toast.error('Fehler beim Ablehnen', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Rezept...</p>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-lg border border-border bg-neutral-800 p-6 space-y-4">
          <div className="flex items-center gap-3 text-red-500">
            <XCircle className="w-6 h-6" />
            <h1 className="text-2xl font-bold">Rezept nicht gefunden</h1>
          </div>
          <p className="text-muted-foreground">
            {error || 'Das gesuchte Rezept existiert nicht.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Zum Canvas
          </Link>
        </div>
      </div>
    );
  }

  const cost = Math.ceil(recipe.pixels.length / 3);

  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'text-yellow-500',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/50',
      label: 'Ausstehend'
    },
    accepted: {
      icon: CheckCircle2,
      color: 'text-green-500',
      bg: 'bg-green-500/10',
      border: 'border-green-500/50',
      label: 'Akzeptiert'
    },
    declined: {
      icon: XCircle,
      color: 'text-red-500',
      bg: 'bg-red-500/10',
      border: 'border-red-500/50',
      label: 'Abgelehnt'
    }
  }[recipe.status];

  const StatusIcon = statusConfig.icon;

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 border-b border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80">
        <div className="flex items-center justify-between px-3 md:px-4 h-14">
          <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-base md:text-lg font-semibold truncate">Rezept-Vorschau</h1>
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border ${statusConfig.border} ${statusConfig.bg} flex-shrink-0`}>
              <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
              <span className={`text-sm font-medium ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4 text-sm text-muted-foreground flex-shrink-0">
            <div className="hidden md:block">{recipe.pixels.length} Pixel</div>
            <div className="hidden md:block">•</div>
            <div className="hidden md:block">{cost}€</div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile Status Bar */}
        <div className="sm:hidden border-t border-border px-3 py-2 flex items-center justify-between bg-neutral-800/50">
          <div className={`flex items-center gap-2 px-2 py-1 rounded-full border ${statusConfig.border} ${statusConfig.bg}`}>
            <StatusIcon className={`w-3 h-3 ${statusConfig.color}`} />
            <span className={`text-xs font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {recipe.pixels.length} Pixel • {cost}€
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className={`absolute inset-0 pt-14 sm:pt-14 md:pt-14 ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          <PixelCanvas
            canvas={canvas}
            highlightPixels={recipe.pixels}
            pixelSize={zoom}
            onPixelHover={handlePixelHover}
            canvasOpacity={0.4}
          />
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-2 md:px-3 py-2 shadow-lg">
          <button
            onClick={() => setZoom(prev => Math.max(2, prev - 1))}
            disabled={zoom <= 2}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Rauszoomen"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <span className="text-sm text-muted-foreground min-w-[50px] md:min-w-[60px] text-center">
            {zoom}x
          </span>

          <button
            onClick={() => setZoom(prev => Math.min(12, prev + 1))}
            disabled={zoom >= 12}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reinzoomen"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-border mx-1" />

          <button
            onClick={resetView}
            className="inline-flex items-center justify-center rounded-md px-2 md:px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Ansicht zurücksetzen"
          >
            <Hand className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Zurücksetzen</span>
          </button>
        </div>
      </div>

      {/* Coordinates Display */}
      {hoverCoords && (
        <div className="hidden sm:block absolute top-20 left-4 z-10 bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border shadow-lg">
          <div className="text-sm font-mono space-y-1">
            <div>
              <span className="text-muted-foreground">Canvas:</span>{' '}
              <span className="font-semibold">X: {hoverCoords.x}</span>
              <span className="text-muted-foreground mx-2">Y: {hoverCoords.y}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Minecraft:</span>{' '}
              <span className="font-semibold">X: {-1392 + hoverCoords.x}</span>
              <span className="text-muted-foreground mx-2">Z: {-1380 + hoverCoords.y}</span>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Details Panel - Desktop: right side, Mobile: slide up */}
      <div className={`absolute z-10 transition-transform duration-300 ${
        showDetails ? 'translate-y-0' : 'translate-y-full md:translate-y-0'
      } bottom-0 left-0 right-0 md:right-4 md:top-20 md:left-auto md:bottom-auto md:w-80`}>
        <div className="rounded-t-lg md:rounded-lg border-t md:border border-border bg-neutral-800/98 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/95 p-4 shadow-lg space-y-4 max-h-[70vh] md:max-h-none overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Rezept-Details</h3>
            <button
              onClick={() => setShowDetails(false)}
              className="md:hidden text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Schließen
            </button>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Erstellt:</span>
              <span>{new Date(recipe.createdAt).toLocaleString('de-DE')}</span>
            </div>
            {recipe.processedAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verarbeitet:</span>
                <span>{new Date(recipe.processedAt).toLocaleString('de-DE')}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pixel:</span>
              <span className="font-bold">{recipe.pixels.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Kosten:</span>
              <span className="font-bold">{cost}€</span>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <div className="text-sm font-medium mb-2">Pixel-Änderungen</div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {recipe.pixels.map((pixel, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <div
                    className="w-4 h-4 border border-border rounded flex-shrink-0"
                    style={{ backgroundColor: pixel.color }}
                  />
                  <div className="font-mono text-muted-foreground">
                    <div>Canvas: ({pixel.x}, {pixel.y})</div>
                    <div className="text-[10px]">MC: ({-1392 + pixel.x}, {-1380 + pixel.y})</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {recipe.status === 'pending' && (
            <div className="pt-2 border-t border-border space-y-3">
              {token ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Überprüfe dieses Rezept und entscheide, ob du es akzeptieren oder ablehnen möchtest.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleAccept}
                      disabled={processing}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {processing ? 'Verarbeite...' : 'Akzeptieren'}
                    </button>
                    <button
                      onClick={handleDecline}
                      disabled={processing}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      {processing ? 'Verarbeite...' : 'Ablehnen'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Dieses Rezept wartet auf die Überprüfung durch den Streamer. Sobald es akzeptiert wurde, erscheinen die Änderungen auf dem Canvas!
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Help Text - Hidden on mobile */}
      <div className="hidden sm:block absolute bottom-4 right-4 z-10 text-xs text-muted-foreground bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border">
        <div>Ziehen zum Schwenken • Strg/Cmd + Scrollen zum Zoomen</div>
      </div>
    </div>
  );
}
